import { useCallback, useEffect, useState } from "react";
import { agentAutomaticFailureSchema } from "@opencloud/agent-protocol";
import { api } from "./api.js";
import { asSentence } from "./agent-models.js";
import type { AgentAutomaticFailure, AiAutomaticStatus, AiChoiceLabel, AiModelChoice } from "./types.js";

/**
 * Automatic tasks that couldn't run (decision 0031): automatic repairs,
 * scheduled tasks and AI calls from the app's code report in the app's chat
 * instead of moving to another provider. A card reads the app's current
 * automatic choice, so once that can run the card says so in place.
 */

/** A retained failure this bundle understands; a newer kind keeps its stored sentence. */
export function knownAutomaticFailure(value: unknown): AgentAutomaticFailure | null {
  if (value === undefined || value === null) return null;
  const parsed = agentAutomaticFailureSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** A light shape check; the service owns validation. */
export function isAiAutomaticStatus(value: unknown): value is AiAutomaticStatus {
  const candidate = value as Partial<AiAutomaticStatus> | null;
  return Boolean(
    candidate &&
      typeof candidate === "object" &&
      typeof candidate.source === "string" &&
      "choice" in candidate &&
      "problem" in candidate,
  );
}

/**
 * The app's automatic choice now (`GET /v1/apps/:appId/ai-automatic`). Reads
 * nothing without an app; `refreshKey` and `refresh()` read it again, for
 * example after the person's pick was saved.
 */
export function useAppAutomaticStatus(
  appId: string | null,
  refreshKey: unknown = null,
): { status: AiAutomaticStatus | null; refresh: () => void } {
  const [state, setState] = useState<{ appId: string | null; status: AiAutomaticStatus | null }>(
    { appId, status: null });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!appId) return;
    const abort = new AbortController();
    void api<unknown>(`/v1/apps/${encodeURIComponent(appId)}/ai-automatic`, { signal: abort.signal })
      .then((value) => {
        if (!abort.signal.aborted && isAiAutomaticStatus(value)) setState({ appId, status: value });
      }, () => undefined);
    return () => abort.abort();
  }, [appId, revision, refreshKey]);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  return { status: appId && state.appId === appId ? state.status : null, refresh };
}

export function choiceName(label: AiChoiceLabel | null, choice: AiModelChoice | null): string | null {
  if (label) return `${label.modelLabel} via ${label.providerLabel}`;
  return choice ? choice.model.slice(choice.model.indexOf("/") + 1) : null;
}

/** Whether the app's automatic tasks can run now, so an earlier failure is behind it. */
export function automaticTasksCanRun(status: AiAutomaticStatus | null): boolean {
  return Boolean(status && status.problem === null && status.choice !== null);
}

function calls(count: number): string {
  return count === 1 ? "1 call" : `${count} calls`;
}

export interface AutomaticFailureView {
  resolved: boolean;
  title: string;
  /** The signal a repair answered, or the scheduled task's title. */
  subject: string | null;
  status: string;
  time: { label: string; dateTime: string };
}

/** What one failure card says, given the app's automatic choice now. */
export function automaticFailureView(
  failure: AgentAutomaticFailure,
  input: { appName: string; status: AiAutomaticStatus | null; formatTime: (value: string) => string },
): AutomaticFailureView {
  const resolved = automaticTasksCanRun(input.status);
  const needed = failure.choice ? `${failure.choice.modelLabel} via ${failure.choice.providerLabel}` : null;
  const reason = asSentence(failure.reason.message);
  const now = input.status ? choiceName(input.status.label, input.status.choice) : null;
  if (failure.kind === "features") {
    const since = input.formatTime(failure.since);
    const last = input.formatTime(failure.lastAt);
    return {
      resolved,
      title: `AI calls in ${input.appName} ${resolved ? "were" : "are"} failing`,
      subject: null,
      status: resolved
        ? `AI calls work again with ${now}. ${calls(failure.failedCalls)} failed before the change.`
        : `${calls(failure.failedCalls)} failed, the last at ${last}. ${needed ? `They need ${needed}: ${reason}` : reason} ` +
          "The app's code got an error for each call, and no other provider was tried.",
      time: resolved
        ? { label: `${since}–${last}`, dateTime: failure.since }
        : { label: `Since ${since}`, dateTime: failure.since },
    };
  }
  return {
    resolved,
    title: failure.kind === "repair" ? "Automatic repair couldn't start" : "Scheduled task couldn't start",
    subject: failure.kind === "repair" ? failure.signal : failure.taskTitle,
    status: resolved
      ? `Automatic tasks can run again with ${now}.`
      : `${needed ? `It needed ${needed}: ${reason}` : reason} No other provider was tried.`,
    time: { label: input.formatTime(failure.occurredAt), dateTime: failure.occurredAt },
  };
}
